import React, { createContext, useContext, useReducer, useMemo, useCallback } from "react";
import { DEFAULT_PAGE, orientedPage } from "../lib/pageFormats";
import { DEFAULT_PIECE } from "../lib/pieceFormats";
import { computeLayout } from "../lib/imposition";

const DEFAULT_CUT_MARKS = {
  enabled: true,
  length_mm: 3,
  weight_pt: 0.25,
  distance_mm: 1,
  pages: "all", // 'all' | 'first' | 'odd' | 'even'
  showBorderFrame: false,
  // 'piece' (current — marks per piece corners)
  // 'block_outer' (only outer corners of the whole imposition block)
  kind: "piece",
};

export const INITIAL_STATE = {
  page: { ...DEFAULT_PAGE },
  piece: { ...DEFAULT_PIECE },
  copies: 24,
  separation: { h_mm: 0, v_mm: 0 },
  bleed: 0, // 0|1|2|3|5
  cutMarks: DEFAULT_CUT_MARKS,
  images: [], // {id, name, dataUrl, width_px, height_px}
  distribution: "duplicate", // 'duplicate' | 'sequence'
  slotAdjustments: {}, // index -> {imageId, fit, zoom, offset_x, offset_y, rotation, distort, scaleX, scaleY}
  // Global block shift per page (in mm). pageOffsets[pageIndex] = { x, y }.
  // Used to compensate real printer misalignment.
  pageOffsets: {},
  selectedSlot: null,
  currentPage: 0,
  previewZoom: 1,
};

export const DEFAULT_ADJ = {
  imageId: null,
  fit: "fill",
  zoom: 1,
  offset_x: 0,
  offset_y: 0,
  rotation: 0,
  // Optional non-proportional scale. Only applied when `distort === true`.
  distort: false,
  scaleX: 1,
  scaleY: 1,
};

function regenerateAssignments(state) {
  const layout = computeLayout({
    page: state.page,
    piece: state.piece,
    copies: state.copies,
    separation: state.separation,
    bleed: state.bleed,
  });
  const total = layout.perPage * layout.pages;
  const visible = Math.min(state.copies, total);
  const next = { ...state.slotAdjustments };

  if (state.images.length === 0) {
    // Keep any manual adjustments but clear imageId
    for (let i = 0; i < visible; i++) {
      if (!next[i]) next[i] = { ...DEFAULT_ADJ };
      next[i] = { ...next[i], imageId: null };
    }
  } else if (state.distribution === "duplicate") {
    const imgId = state.images[0].id;
    for (let i = 0; i < visible; i++) {
      const prev = next[i] || { ...DEFAULT_ADJ };
      // Only override imageId if currently null OR was tracking previous global image.
      // For simplicity, in duplicate mode always set imageId to images[0].id
      next[i] = { ...prev, imageId: imgId };
    }
  } else if (state.distribution === "page_per_image") {
    // Each page is filled entirely with a single image. Image index = pageIndex % imageCount.
    const perPage = Math.max(1, Math.floor(layout.perPage || 1));
    for (let i = 0; i < visible; i++) {
      const pageIdx = Math.floor(i / perPage);
      const img = state.images[pageIdx % state.images.length];
      const prev = next[i] || { ...DEFAULT_ADJ };
      next[i] = { ...prev, imageId: img.id };
    }
  } else {
    // sequence
    for (let i = 0; i < visible; i++) {
      const img = state.images[i % state.images.length];
      const prev = next[i] || { ...DEFAULT_ADJ };
      next[i] = { ...prev, imageId: img.id };
    }
  }
  // Remove out-of-range adjustments
  for (const k of Object.keys(next)) {
    if (Number(k) >= visible) delete next[k];
  }
  return next;
}

function reducer(state, action) {
  switch (action.type) {
    case "SET_PAGE": {
      const merged = { ...state.page, ...action.payload };
      const nextState = { ...state, page: merged, currentPage: 0 };
      return { ...nextState, slotAdjustments: regenerateAssignments(nextState) };
    }
    case "SET_PIECE": {
      const merged = { ...state.piece, ...action.payload };
      const nextState = { ...state, piece: merged, currentPage: 0 };
      return { ...nextState, slotAdjustments: regenerateAssignments(nextState) };
    }
    case "SET_COPIES": {
      const nextState = { ...state, copies: Math.max(1, action.payload), currentPage: 0 };
      return { ...nextState, slotAdjustments: regenerateAssignments(nextState) };
    }
    case "SET_SEPARATION": {
      const nextState = { ...state, separation: { ...state.separation, ...action.payload } };
      return { ...nextState, slotAdjustments: regenerateAssignments(nextState) };
    }
    case "SET_BLEED": {
      return { ...state, bleed: action.payload };
    }
    case "SET_CUT_MARKS": {
      return { ...state, cutMarks: { ...state.cutMarks, ...action.payload } };
    }
    case "ADD_IMAGES": {
      const nextState = { ...state, images: [...state.images, ...action.payload] };
      return { ...nextState, slotAdjustments: regenerateAssignments(nextState) };
    }
    case "REMOVE_IMAGE": {
      const remaining = state.images.filter((i) => i.id !== action.payload);
      const cleaned = { ...state.slotAdjustments };
      for (const k of Object.keys(cleaned)) {
        if (cleaned[k].imageId === action.payload) {
          cleaned[k] = { ...cleaned[k], imageId: null };
        }
      }
      const nextState = { ...state, images: remaining, slotAdjustments: cleaned };
      return { ...nextState, slotAdjustments: regenerateAssignments(nextState) };
    }
    case "SET_DISTRIBUTION": {
      const nextState = { ...state, distribution: action.payload };
      return { ...nextState, slotAdjustments: regenerateAssignments(nextState) };
    }
    case "UPDATE_SLOT": {
      const { index, patch } = action.payload;
      const prev = state.slotAdjustments[index] || { ...DEFAULT_ADJ };
      return {
        ...state,
        slotAdjustments: {
          ...state.slotAdjustments,
          [index]: { ...prev, ...patch },
        },
      };
    }
    case "APPLY_TO_ALL": {
      const { patch } = action.payload;
      const next = {};
      for (const k of Object.keys(state.slotAdjustments)) {
        next[k] = { ...state.slotAdjustments[k], ...patch };
      }
      return { ...state, slotAdjustments: next };
    }
    case "SELECT_SLOT": {
      return { ...state, selectedSlot: action.payload };
    }
    case "SET_CURRENT_PAGE": {
      return { ...state, currentPage: action.payload, selectedSlot: null };
    }
    case "SET_PREVIEW_ZOOM": {
      return { ...state, previewZoom: action.payload };
    }
    case "SET_PAGE_OFFSET": {
      const { pageIndex, x, y } = action.payload;
      return {
        ...state,
        pageOffsets: {
          ...state.pageOffsets,
          [pageIndex]: { x, y },
        },
      };
    }
    case "APPLY_OFFSET_TO_SCOPE": {
      // scope: 'all' | 'odd' | 'even'. Uses 1-based page numbering for odd/even.
      const { scope, x, y } = action.payload;
      const totalPages = action.payload.totalPages || 1;
      const next = { ...state.pageOffsets };
      for (let i = 0; i < totalPages; i++) {
        const oneBased = i + 1;
        if (scope === "all" || (scope === "odd" && oneBased % 2 === 1) || (scope === "even" && oneBased % 2 === 0)) {
          next[i] = { x, y };
        }
      }
      return { ...state, pageOffsets: next };
    }
    case "RESET_PAGE_OFFSETS": {
      return { ...state, pageOffsets: {} };
    }
    case "SWAP_PIECE_DIMS": {
      const { width_mm, height_mm } = state.piece;
      const nextState = {
        ...state,
        piece: { ...state.piece, width_mm: height_mm, height_mm: width_mm },
        currentPage: 0,
      };
      return { ...nextState, slotAdjustments: regenerateAssignments(nextState) };
    }
    case "FILL_SHEET": {
      // Set copies = perPage so a single page is fully filled.
      const layout = computeLayout({
        page: orientedPage(state.page),
        piece: state.piece,
        copies: 9999,
        separation: state.separation,
        bleed: state.bleed,
      });
      const nextCopies = Math.max(1, layout.perPage);
      const nextState = { ...state, copies: nextCopies, currentPage: 0 };
      return { ...nextState, slotAdjustments: regenerateAssignments(nextState) };
    }
    case "LOAD_PROJECT": {
      return { ...action.payload, selectedSlot: null, currentPage: 0 };
    }
    case "NEW_PROJECT": {
      return { ...INITIAL_STATE };
    }
    default:
      return state;
  }
}

const ProjectContext = createContext(null);

export function ProjectProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  const orientedPageMemo = useMemo(() => orientedPage(state.page), [state.page]);

  const layout = useMemo(
    () =>
      computeLayout({
        page: orientedPageMemo,
        piece: state.piece,
        copies: state.copies,
        separation: state.separation,
        bleed: state.bleed,
      }),
    [orientedPageMemo, state.piece, state.copies, state.separation, state.bleed]
  );

  const actions = useMemo(
    () => ({
      setPage: (patch) => dispatch({ type: "SET_PAGE", payload: patch }),
      setPiece: (patch) => dispatch({ type: "SET_PIECE", payload: patch }),
      setCopies: (n) => dispatch({ type: "SET_COPIES", payload: n }),
      setSeparation: (patch) => dispatch({ type: "SET_SEPARATION", payload: patch }),
      setBleed: (n) => dispatch({ type: "SET_BLEED", payload: n }),
      setCutMarks: (patch) => dispatch({ type: "SET_CUT_MARKS", payload: patch }),
      addImages: (imgs) => dispatch({ type: "ADD_IMAGES", payload: imgs }),
      removeImage: (id) => dispatch({ type: "REMOVE_IMAGE", payload: id }),
      setDistribution: (m) => dispatch({ type: "SET_DISTRIBUTION", payload: m }),
      updateSlot: (index, patch) => dispatch({ type: "UPDATE_SLOT", payload: { index, patch } }),
      applyToAll: (patch) => dispatch({ type: "APPLY_TO_ALL", payload: { patch } }),
      selectSlot: (i) => dispatch({ type: "SELECT_SLOT", payload: i }),
      setCurrentPage: (i) => dispatch({ type: "SET_CURRENT_PAGE", payload: i }),
      setPreviewZoom: (z) => dispatch({ type: "SET_PREVIEW_ZOOM", payload: z }),
      setPageOffset: (pageIndex, x, y) =>
        dispatch({ type: "SET_PAGE_OFFSET", payload: { pageIndex, x, y } }),
      applyOffsetToScope: (scope, x, y, totalPages) =>
        dispatch({ type: "APPLY_OFFSET_TO_SCOPE", payload: { scope, x, y, totalPages } }),
      resetPageOffsets: () => dispatch({ type: "RESET_PAGE_OFFSETS" }),
      swapPieceDims: () => dispatch({ type: "SWAP_PIECE_DIMS" }),
      fillSheet: () => dispatch({ type: "FILL_SHEET" }),
      loadProject: (p) => dispatch({ type: "LOAD_PROJECT", payload: p }),
      newProject: () => dispatch({ type: "NEW_PROJECT" }),
    }),
    []
  );

  const value = useMemo(
    () => ({ state, layout, page: orientedPageMemo, ...actions }),
    [state, layout, orientedPageMemo, actions]
  );

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

export function useProject() {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error("useProject must be inside ProjectProvider");
  return ctx;
}
